import React, {
    useState,
    useEffect,
    useCallback,
  } from "react";
  import { FiDownload, FiPlus, FiUpload } from "react-icons/fi";
  import { toast } from "react-toastify";
  import adminService from "../services/adminService";
  
  import InfluencerTable from "./InfluencerTable";
  import InfluencerFormModal from "./InfluencerFormModal";
  import InfluencerViewModal from "./InfluencerViewModal";
  import UploadModal from "./UploadModal";
  
  const initialFormState = {
    username: "",
    name: "",
    email: "",
    mobile: "",
    title: "",
    rating: 0,
    price: 0,
    platform: [],
    category: "",
    location: "",
    gender: "",
    age: "",
    language: "",
    image: "",
    avatar: "",
    bio: "",
    reviewCount: 0,
    followers: 0,
    following: 0,
    posts: 0,
    contentType: "",
    packages: [],
    analytics: {},
    portfolio: [],
    reviews: [],
    faqs: [],
    isActive: true
  };
  
  const MarketPlace = () => {
    const [influencers, setInfluencers] = useState([]);
    const [pagination, setPagination] = useState({
      page: 1,
      limit: 10,
      total: 0,
      pages: 0
    });
  
    const [loading, setLoading] = useState(false);
    const [statistics, setStatistics] = useState(null);
  
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
  
    const [filters, setFilters] = useState({
      category: "",
      platform: "",
      isActive: undefined
    });
  
    const [showForm, setShowForm] = useState(false);
    const [editingInfluencer, setEditingInfluencer] = useState(null);
    const [viewingInfluencer, setViewingInfluencer] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
  
    const [formData, setFormData] = useState(initialFormState);
  
    // ------------------ DEBOUNCE SEARCH ------------------
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedSearch(searchTerm);
        setPagination((prev) => ({ ...prev, page: 1 })); // Reset page on search
      }, 500);
  
      return () => clearTimeout(timer);
    }, [searchTerm]);
  
    // Reset page when filters change
    useEffect(() => {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, [filters]);
  
    // ------------------ FETCH INFLUENCERS ------------------
    const fetchInfluencers = useCallback(async () => {
      setLoading(true);
      try {
        const res = await adminService.getMarketplaceInfluencers(
          pagination.page,
          pagination.limit,
          debouncedSearch,
          filters.category,
          filters.platform,
          filters.isActive
        );
  
        if (res.success) {
          setInfluencers(res.data.influencers);
          setPagination(res.data.pagination);
        }
      } catch (err) {
        toast.error(err.error || "Failed to fetch influencers");
      } finally {
        setLoading(false);
      }
    }, [pagination.page, pagination.limit, debouncedSearch, filters]);
  
    useEffect(() => {
      fetchInfluencers();
    }, [fetchInfluencers]);
  
    // ------------------ FETCH STATS (ONCE) ------------------
    useEffect(() => {
      const fetchStats = async () => {
        try {
          const res = await adminService.getMarketplaceStatistics();
          if (res.success) setStatistics(res.data);
        } catch {
          console.error("Failed to fetch stats");
        }
      };
      fetchStats();
    }, []);
  
    // ------------------ CRUD HANDLERS ------------------
    const handleDelete = useCallback(
      async (id) => {
        if (!window.confirm("Are you sure?")) return;
  
        try {
          await adminService.deleteMarketplaceInfluencer(id);
          toast.success("Deleted successfully");
          fetchInfluencers();
        } catch (err) {
          toast.error(err.error || "Delete failed");
        }
      },
      [fetchInfluencers]
    );
  
    const handleToggleStatus = useCallback(
      async (id) => {
        try {
          await adminService.toggleMarketplaceInfluencerStatus(id);
          toast.success("Status updated");
          fetchInfluencers();
        } catch (err) {
          toast.error(err.error || "Update failed");
        }
      },
      [fetchInfluencers]
    );
  
    const handleEdit = useCallback(async (id) => {
      try {
        const res = await adminService.getMarketplaceInfluencerById(id);
        if (res.success) {
          setEditingInfluencer(res.data);
          setFormData(res.data);
          setShowForm(true);
        }
      } catch (err) {
        toast.error("Failed to load influencer");
      }
    }, []);
  
    const handleSubmit = useCallback(async () => {
      try {
        if (editingInfluencer) {
          await adminService.updateMarketplaceInfluencer(
            editingInfluencer._id,
            formData
          );
          toast.success("Updated successfully");
        } else {
          await adminService.createMarketplaceInfluencer(formData);
          toast.success("Created successfully");
        }
  
        setShowForm(false);
        setFormData(initialFormState);
        setEditingInfluencer(null);
        fetchInfluencers();
      } catch (err) {
        toast.error(err.error || "Save failed");
      }
    }, [editingInfluencer, formData, fetchInfluencers]);

    const handleExport = useCallback(async () => {
      setIsExporting(true);
      try {
        const res = await adminService.exportMarketplaceInfluencers(
          debouncedSearch,
          filters.category,
          filters.platform,
          filters.isActive
        );

        const blob = new Blob([res.data], {
          type: res.mimeType || "application/zip"
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", res.filename || "marketplace-influencers.zip");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success("Marketplace influencers ZIP exported successfully");
      } catch (err) {
        toast.error(err.error || "Failed to export marketplace influencers");
      } finally {
        setIsExporting(false);
      }
    }, [debouncedSearch, filters]);
  
    // Memo safe page handler
    const handlePageChange = useCallback((page) => {
      setPagination((prev) => ({ ...prev, page }));
    }, []);

    const handlePageSizeChange = useCallback((limit) => {
      setPagination((prev) => ({ ...prev, limit, page: 1 }));
    }, []);
  
    return (
      <div className="space-y-6">
  
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-center">
          <h1 className="text-lg md:text-xl font-bold">Marketplace Influencers</h1>
  
          <div className="flex gap-2 md:gap-3">
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="flex text-xs md:text-sm items-center gap-2 px-2 md:px-4 py-2 md:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <FiDownload /> {isExporting ? "Exporting..." : "Export ZIP"}
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex text-xs md:text-sm items-center gap-2 px-2 md:px-4 py-2 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <FiUpload /> Upload
            </button>
  
            <button
              onClick={() => {
                setEditingInfluencer(null);
                setFormData(initialFormState);
                setShowForm(true);
              }}
              className="flex text-xs md:text-sm items-center gap-2 px-2 md:px-4 py-2 md:py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
            >
              <FiPlus /> Add
            </button>
          </div>
        </div>

        {/* TABLE */}
        <InfluencerTable
          influencers={influencers}
          pagination={pagination}
          loading={loading}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onDelete={handleDelete}
          onToggle={handleToggleStatus}
          onEdit={handleEdit}
          onView={setViewingInfluencer}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          statistics={statistics}
        />
  
        {/* MODALS */}
        {showForm && (
          <InfluencerFormModal
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onClose={() => setShowForm(false)}
            editing={editingInfluencer}
          />
        )}
  
        {viewingInfluencer && (
          <InfluencerViewModal
            influencer={viewingInfluencer}
            onClose={() => setViewingInfluencer(null)}
          />
        )}
  
        {showUploadModal && (
          <UploadModal
            onClose={() => setShowUploadModal(false)}
            onSuccess={fetchInfluencers}
          />
        )}
      </div>
    );
  };
  
  export default MarketPlace;
  